/** States where the outcome has happened and no response is left. */
export const SPENT = new Set([
  "deemed", "time_barred", "answered_in_time", "answered_late", "preserved",
]);

export type Tone = "now" | "high" | "open" | "spent";

/**
 * How loudly a clock should read.
 *
 * Keyed on the contract rather than on how late something is, because the
 * contract makes a distinction the day count does not: a reply period that
 * has run out is somebody being late, and nothing happens by itself. A
 * further period running towards a dated automatic outcome is different in
 * kind — the outcome lands whether or not anybody acts, and once it lands
 * it cannot be undone.
 *
 * On the pilot that is one row in nine rather than seven, which is the
 * difference between a signal and an alarm flood. It is also simply true:
 * only CE-014 is running out on its own today.
 */
export interface Clocked {
  state: string | null;
  deemed_date: string | null;
  /** Days to the next deadline, where the caller already has it. */
  days?: number | null;
  /** Or the date itself, where it does not — the board carries this. */
  due_date?: string | null;
}

export function toneFor(row: Clocked): Tone {
  if (row.state && SPENT.has(row.state)) return "spent";
  // An automatic outcome is dated and still ahead.
  if (row.deemed_date) return "now";
  const days = row.days ?? daysUntil(row.due_date);
  if (days !== null && days <= 0) return "high";
  return "open";
}

/** Whole days from today to an ISO date, or null if there is not one. */
function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return null;
  const today = new Date();
  const midnight = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((then - midnight) / 86400000);
}
